import React, { useState, useRef, useMemo } from 'react';
import { FileText, Upload, X, AlertCircle, CheckCircle2, PlusCircle, ArrowRightLeft, Info } from 'lucide-react';
import SupplierCombobox from './SupplierCombobox';
import {
    TimberItem,
    TimberItemInput,
    TimberMoistureContent,
    importTimberItems,
    updateTimberItem,
    normalizeMoistureContent,
} from '../../utils/materialsDatabaseService';
import './SupplierImportModal.css';

type ImportFormat = 'piece' | 'mbf';

interface ParsedRow {
    dimensions: string;
    species?: string;
    grade?: string;
    moistureContent: TimberMoistureContent;
    piecePrice: number;
    costPerBdFt: number;
    boardFeet: number;
    rawLine: string;
}

type MatchStatus = 'matched' | 'updated' | 'new';

interface PreviewRow extends ParsedRow {
    status: MatchStatus;
    matchedItem?: TimberItem;       // existing item if matched
    oldPiecePrice?: number;         // previous price if updating
    priceChangePct?: number;        // % delta
}

interface SupplierImportModalProps {
    isOpen: boolean;
    onClose: () => void;
    existingItems: TimberItem[];
    supplierNames: string[];
}

// Parse dimensions (HxWxL where H,W in inches, L in feet) to board feet
const parseDimensions = (dimensions: string): number => {
    const match = dimensions.match(/(\d+\.?\d*)\s*[xX×]\s*(\d+\.?\d*)\s*[xX×]\s*(\d+\.?\d*)/);
    if (!match) return 0;
    const height = parseFloat(match[1]);
    const width = parseFloat(match[2]);
    const length = parseFloat(match[3]);
    return (height * width * length) / 12;
};

// Normalize dimensions for matching: strip spaces, lowercase x
const normalizeDimensions = (dim: string): string => {
    return dim
        .replace(/\s+/g, '')
        .replace(/[×X]/g, 'x')
        .replace(/["']/g, '')
        .toLowerCase();
};

const GRADE_OPTIONS = ['#1', '#2', 'Select', 'FAS', 'Standard', 'Construction', 'Utility'];

function splitDataLine(line: string): string[] {
    if (line.includes('\t')) return line.split('\t').map(v => v.trim());
    return line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
}

/** Map cell text to stored dryness; empty → '' (unspecified). */
function parseDrynessCell(text: string): TimberMoistureContent {
    const t = (text || '').trim();
    if (!t) return '';
    const n = normalizeMoistureContent(t);
    if (n === 'green') return 'green';
    if (n === 'kiln_dried') return 'kiln_dried';
    return '';
}

function drynessPreviewLabel(m: TimberMoistureContent): string {
    if (m === 'green') return 'Green';
    if (m === 'kiln_dried') return 'Kiln dried';
    return '—';
}

const SupplierImportModal: React.FC<SupplierImportModalProps> = ({
    isOpen,
    onClose,
    existingItems,
    supplierNames,
}) => {
    const [supplier, setSupplier] = useState('');
    const [species, setSpecies] = useState('');
    const [grade, setGrade] = useState('#1');
    const [defaultDryness, setDefaultDryness] = useState<TimberMoistureContent>('');
    const [format, setFormat] = useState<ImportFormat>('piece');
    const [csvText, setCsvText] = useState('');
    const [preview, setPreview] = useState<PreviewRow[]>([]);
    const [error, setError] = useState('');
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Unique species in existing items
    const speciesNames = useMemo(() =>
        Array.from(new Set(existingItems.map(i => i.species).filter(Boolean))).sort()
        , [existingItems]);

    if (!isOpen) return null;

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (event) => {
            const text = event.target?.result as string;
            setCsvText(text);
            parseAndMatch(text);
        };
        reader.readAsText(file);
    };

    const parseAndMatch = (text: string, drynessOverride?: TimberMoistureContent) => {
        setError('');
        setImportResult('');
        const lines = text.trim().split('\n');
        if (lines.length === 0) {
            setError('No data to import');
            return;
        }

        const moistureDefault = drynessOverride !== undefined ? drynessOverride : defaultDryness;

        // Detect header row
        const firstLine = lines[0].toLowerCase();
        const hasHeader = firstLine.includes('size') || firstLine.includes('dim') ||
            firstLine.includes('price') || firstLine.includes('species') ||
            firstLine.includes('board') || firstLine.includes('mbf') ||
            firstLine.includes('dry') || firstLine.includes('moisture') ||
            firstLine.includes('kiln') || firstLine.includes('green');
        const startIndex = hasHeader ? 1 : 0;

        const headerCells = hasHeader ? splitDataLine(lines[0]).map(h => h.toLowerCase().trim()) : [];
        const colIdx = (patterns: string[]): number => {
            for (let j = 0; j < headerCells.length; j++) {
                if (patterns.some(p => headerCells[j].includes(p))) return j;
            }
            return -1;
        };
        const headerPiece = (format === 'piece' && headerCells.length > 0)
            ? {
                dim: colIdx(['size', 'dim', 'dimension', 'hxwxl']),
                price: colIdx(['piece', 'price', 'ea', 'each', 'total']),
                bdft: colIdx(['$/bdft', '$/bf', 'bdft', '/bdft', 'per bdft', 'per bf']),
                dry: colIdx(['dryness', 'moisture', 'green', 'kiln', 'kd']),
            }
            : null;
        const headerMbf = (format === 'mbf' && headerCells.length > 0)
            ? {
                dim: colIdx(['size', 'dim', 'dimension']),
                mbf: colIdx(['mbf', 'mfb', 'msf', 'price']),
                dry: colIdx(['dryness', 'moisture', 'green', 'kiln', 'kd']),
            }
            : null;

        const parsed: ParsedRow[] = [];

        for (let i = startIndex; i < lines.length; i++) {
            const line = lines[i].trim();
            if (!line) continue;

            const cleaned = splitDataLine(line);

            if (cleaned.length < 2) {
                setError(`Line ${i + 1}: Expected at least 2 columns`);
                continue;
            }

            if (format === 'piece') {
                let dimensions: string;
                let piecePrice: number;
                let costPerBdFt = 0;
                let moistureContent: TimberMoistureContent = moistureDefault;

                if (headerPiece && headerPiece.dim >= 0 && headerPiece.price >= 0 && cleaned.length > Math.max(headerPiece.dim, headerPiece.price)) {
                    dimensions = cleaned[headerPiece.dim] || '';
                    piecePrice = parseFloat((cleaned[headerPiece.price] || '').replace(/[$,]/g, '')) || 0;
                    if (headerPiece.bdft !== undefined && headerPiece.bdft >= 0 && cleaned[headerPiece.bdft]) {
                        costPerBdFt = parseFloat(cleaned[headerPiece.bdft].replace(/[$,]/g, '')) || 0;
                    }
                    if (headerPiece.dry !== undefined && headerPiece.dry >= 0 && cleaned[headerPiece.dry]) {
                        moistureContent = parseDrynessCell(cleaned[headerPiece.dry]) || moistureDefault;
                    }
                } else {
                    dimensions = cleaned[0];
                    piecePrice = parseFloat(cleaned[1].replace(/[$,]/g, '')) || 0;

                    if (cleaned.length === 2) {
                        costPerBdFt = 0;
                    } else if (cleaned.length === 3) {
                        const dryTry = parseDrynessCell(cleaned[2]);
                        if (dryTry !== '' || normalizeMoistureContent(cleaned[2]) !== 'unspecified') {
                            moistureContent = dryTry || moistureDefault;
                        } else {
                            costPerBdFt = parseFloat(cleaned[2].replace(/[$,]/g, '')) || 0;
                        }
                    } else {
                        costPerBdFt = parseFloat(cleaned[2].replace(/[$,]/g, '')) || 0;
                        moistureContent = parseDrynessCell(cleaned[3] || '') || moistureDefault;
                    }
                }

                const boardFeet = parseDimensions(dimensions);

                if (boardFeet === 0) {
                    setError(`Line ${i + 1}: Could not parse dimensions "${dimensions}"`);
                    continue;
                }

                if (!costPerBdFt && boardFeet > 0 && piecePrice > 0) {
                    costPerBdFt = piecePrice / boardFeet;
                }

                parsed.push({
                    dimensions,
                    moistureContent,
                    piecePrice,
                    costPerBdFt,
                    boardFeet: Math.round(boardFeet * 100) / 100,
                    rawLine: line,
                });
            } else {
                let dimensions: string;
                let pricePerMBF: number;
                let moistureContent: TimberMoistureContent = moistureDefault;

                if (headerMbf && headerMbf.dim >= 0 && headerMbf.mbf >= 0 && cleaned.length > Math.max(headerMbf.dim, headerMbf.mbf)) {
                    dimensions = cleaned[headerMbf.dim] || '';
                    pricePerMBF = parseFloat((cleaned[headerMbf.mbf] || '').replace(/[$,]/g, '')) || 0;
                    if (headerMbf.dry !== undefined && headerMbf.dry >= 0 && cleaned[headerMbf.dry]) {
                        moistureContent = parseDrynessCell(cleaned[headerMbf.dry]) || moistureDefault;
                    }
                } else {
                    dimensions = cleaned[0];
                    pricePerMBF = parseFloat(cleaned[1].replace(/[$,]/g, '')) || 0;
                    if (cleaned.length >= 3) {
                        moistureContent = parseDrynessCell(cleaned[2]) || moistureDefault;
                    }
                }

                const boardFeet = parseDimensions(dimensions);

                if (boardFeet === 0) {
                    setError(`Line ${i + 1}: Could not parse dimensions "${dimensions}"`);
                    continue;
                }

                const piecePrice = Math.round((boardFeet * pricePerMBF / 1000) * 100) / 100;
                const costPerBdFt = pricePerMBF / 1000;

                parsed.push({
                    dimensions,
                    moistureContent,
                    piecePrice,
                    costPerBdFt,
                    boardFeet: Math.round(boardFeet * 100) / 100,
                    rawLine: line,
                });
            }
        }

        // Deduplicate by normalized dimensions + dryness (keep first occurrence)
        const seen = new Set<string>();
        const deduped = parsed.filter(row => {
            const key = `${normalizeDimensions(row.dimensions)}|${normalizeMoistureContent(row.moistureContent)}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });

        // Smart match against existing items
        const matchSpecies = species || null;
        const matchGrade = grade || null;

        const previewRows: PreviewRow[] = deduped.map(row => {
            const normalizedDim = normalizeDimensions(row.dimensions);

            // Find matching existing item (same species, grade, dimensions — supplier is inside the array now)
            const mNorm = normalizeMoistureContent(row.moistureContent);
            const match = existingItems.find(item => {
                const dimMatch = normalizeDimensions(item.dimensions) === normalizedDim;
                const speciesMatch = matchSpecies
                    ? item.species.toLowerCase() === matchSpecies.toLowerCase()
                    : true;
                const gradeMatch = matchGrade
                    ? item.grade.toLowerCase() === matchGrade.toLowerCase()
                    : true;
                const moistureMatch = normalizeMoistureContent(item.moistureContent) === mNorm;
                return dimMatch && speciesMatch && gradeMatch && moistureMatch;
            });

            if (match) {
                // Check if this supplier already has a price entry
                const existingSupplierEntry = match.supplierPricing?.find(
                    sp => sp.supplier.toLowerCase() === supplier.toLowerCase()
                );

                if (existingSupplierEntry) {
                    const priceDelta = row.piecePrice - existingSupplierEntry.piecePrice;
                    const priceChangePct = existingSupplierEntry.piecePrice > 0
                        ? (priceDelta / existingSupplierEntry.piecePrice) * 100
                        : 0;

                    if (Math.abs(priceDelta) < 0.01) {
                        return {
                            ...row,
                            status: 'matched' as MatchStatus,
                            matchedItem: match,
                        };
                    } else {
                        return {
                            ...row,
                            status: 'updated' as MatchStatus,
                            matchedItem: match,
                            oldPiecePrice: existingSupplierEntry.piecePrice,
                            priceChangePct: Math.round(priceChangePct * 10) / 10,
                        };
                    }
                } else {
                    // Item exists but this supplier is new to it — treat as "updated" (adding supplier)
                    return {
                        ...row,
                        status: 'updated' as MatchStatus,
                        matchedItem: match,
                        oldPiecePrice: undefined,
                        priceChangePct: undefined,
                    };
                }
            }

            return {
                ...row,
                status: 'new' as MatchStatus,
            };
        });

        setPreview(previewRows);
    };

    const handleImport = async () => {
        if (preview.length === 0 || !species) return;
        setImporting(true);
        setError('');

        try {
            const newItems: TimberItemInput[] = [];
            let updatedCount = 0;

            for (const row of preview) {
                if (row.status === 'new') {
                    // Create new item with supplierPricing array
                    const supplierPricingArr = supplier ? [{
                        supplier: supplier,
                        piecePrice: row.piecePrice,
                        costPerBdFt: row.costPerBdFt,
                    }] : undefined;

                    newItems.push({
                        species: species,
                        grade: grade,
                        dimensions: row.dimensions,
                        moistureContent: row.moistureContent || undefined,
                        piecePrice: row.piecePrice,
                        costPerBdFt: row.costPerBdFt,
                        boardFeet: row.boardFeet,
                        supplier: supplier || undefined,
                        supplierPricing: supplierPricingArr,
                    });
                } else if (row.status === 'updated' && row.matchedItem) {
                    // Update the supplierPricing array on the existing item
                    const existingPricing = row.matchedItem.supplierPricing || [];
                    const supplierIdx = existingPricing.findIndex(
                        sp => sp.supplier.toLowerCase() === supplier.toLowerCase()
                    );

                    let updatedPricing;
                    if (supplierIdx >= 0) {
                        // Update existing supplier entry
                        updatedPricing = [...existingPricing];
                        updatedPricing[supplierIdx] = {
                            ...updatedPricing[supplierIdx],
                            piecePrice: row.piecePrice,
                            costPerBdFt: row.costPerBdFt,
                        };
                    } else {
                        // Add new supplier entry
                        updatedPricing = [
                            ...existingPricing,
                            {
                                supplier: supplier,
                                piecePrice: row.piecePrice,
                                costPerBdFt: row.costPerBdFt,
                            },
                        ];
                    }

                    // Sanitize pricing entries to remove undefined values (Firestore rejects them)
                    const cleanPricing = updatedPricing.map((sp: any) => ({
                        supplier: sp.supplier,
                        piecePrice: sp.piecePrice ?? 0,
                        costPerBdFt: sp.costPerBdFt ?? 0,
                    }));

                    // Also update top-level price if this is the active supplier
                    const updates: any = { supplierPricing: cleanPricing };
                    if (row.matchedItem.supplier === supplier || !row.matchedItem.supplier) {
                        updates.piecePrice = row.piecePrice;
                        updates.costPerBdFt = row.costPerBdFt;
                        updates.supplier = supplier;
                    }

                    await updateTimberItem(row.matchedItem.id, updates);
                    updatedCount++;
                }
                // 'matched' items with same price are skipped
            }

            if (newItems.length > 0) {
                await importTimberItems(newItems);
            }

            const parts: string[] = [];
            if (newItems.length > 0) parts.push(`${newItems.length} new items added`);
            if (updatedCount > 0) parts.push(`${updatedCount} prices updated`);
            const skipped = preview.filter(r => r.status === 'matched').length;
            if (skipped > 0) parts.push(`${skipped} unchanged (skipped)`);

            setImportResult(parts.join(', '));
            setPreview([]);
            setCsvText('');
        } catch (err) {
            console.error('Import error:', err);
            setError('Failed to import items. Check console for details.');
        } finally {
            setImporting(false);
        }
    };

    const handleClose = () => {
        setSupplier('');
        setSpecies('');
        setGrade('#1');
        setDefaultDryness('');
        setFormat('piece');
        setCsvText('');
        setPreview([]);
        setError('');
        setImportResult('');
        onClose();
    };

    const formatCurrency = (val: number) =>
        new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);

    const newCount = preview.filter(r => r.status === 'new').length;
    const updatedCount = preview.filter(r => r.status === 'updated').length;
    const matchedCount = preview.filter(r => r.status === 'matched').length;

    return (
        <div className="import-modal-overlay" onClick={handleClose}>
            <div className="supplier-import-modal" onClick={(e) => e.stopPropagation()}>
                <div className="import-modal-header">
                    <h2><Upload size={24} /> Supplier Price Import</h2>
                    <button className="import-modal-close" onClick={handleClose}>
                        <X size={20} />
                    </button>
                </div>

                <div className="import-modal-body">
                    {/* Step 1: Supplier + Species + Grade */}
                    <div className="import-step">
                        <div className="import-step-label">1. Identify the source</div>

                        <div className="import-field">
                            <label>Supplier *</label>
                            <SupplierCombobox
                                value={supplier}
                                onChange={setSupplier}
                                suggestions={supplierNames}
                                placeholder="Who is this quote from?"
                            />
                        </div>

                        <div className="import-field-row">
                            <div className="import-field">
                                <label>Species *</label>
                                <input
                                    type="text"
                                    value={species}
                                    onChange={(e) => setSpecies(e.target.value)}
                                    placeholder="e.g., Douglas Fir"
                                    list="import-species-list"
                                />
                                <datalist id="import-species-list">
                                    {speciesNames.map(s => (
                                        <option key={s} value={s} />
                                    ))}
                                </datalist>
                            </div>
                            <div className="import-field">
                                <label>Grade *</label>
                                <select
                                    value={grade}
                                    onChange={(e) => setGrade(e.target.value)}
                                    className="import-select"
                                >
                                    {GRADE_OPTIONS.map(g => (
                                        <option key={g} value={g}>{g}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="import-field">
                            <label>Default dryness</label>
                            <select
                                value={defaultDryness}
                                onChange={(e) => {
                                    const v = e.target.value as TimberMoistureContent;
                                    setDefaultDryness(v);
                                    if (csvText.trim()) parseAndMatch(csvText, v);
                                }}
                                className="import-select"
                            >
                                <option value="">Unspecified (legacy)</option>
                                <option value="green">Green</option>
                                <option value="kiln_dried">Kiln dried</option>
                            </select>
                            <span className="field-hint" style={{ display: 'block', marginTop: 6, fontSize: 12, opacity: 0.85 }}>
                                Used when a row has no dryness column. CSV can include a Dryness column to override per row.
                            </span>
                        </div>
                    </div>

                    {/* Step 2: Format + Data */}
                    <div className="import-step">
                        <div className="import-step-label">2. Paste or upload pricing data</div>

                        <div className="import-format-toggle">
                            <button
                                className={`format-btn ${format === 'piece' ? 'active' : ''}`}
                                onClick={() => { setFormat('piece'); if (csvText) parseAndMatch(csvText, defaultDryness); }}
                            >
                                Piece Price
                            </button>
                            <button
                                className={`format-btn ${format === 'mbf' ? 'active' : ''}`}
                                onClick={() => { setFormat('mbf'); if (csvText) parseAndMatch(csvText, defaultDryness); }}
                            >
                                Price per MBF
                            </button>
                        </div>

                        <div className="import-format-hint">
                            <Info size={14} />
                            {format === 'piece'
                                ? 'Format: Size, Piece Price [, $/bdft] [, Dryness]. Or header row with columns like Size, Price, Dryness (green / kiln / KD).'
                                : 'Format: Size, Price per MBF [, Dryness]. Or use a header row with MBF and optional Dryness.'
                            }
                        </div>

                        <div className="import-file-row">
                            <input
                                type="file"
                                accept=".csv,.txt,.tsv"
                                ref={fileInputRef}
                                onChange={handleFileSelect}
                                style={{ display: 'none' }}
                            />
                            <button
                                className="materials-action-btn secondary"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <Upload size={16} /> Choose File
                            </button>
                        </div>

                        <textarea
                            value={csvText}
                            onChange={(e) => {
                                setCsvText(e.target.value);
                                if (e.target.value.trim()) {
                                    parseAndMatch(e.target.value);
                                } else {
                                    setPreview([]);
                                }
                            }}
                            placeholder={format === 'piece'
                                ? "Paste data here:\n8x8x10, 245.00\n8x8x12, 290.00, kiln\n6x6x8, 120.00, green"
                                : "Paste data here:\n8x8x10, 5250\n8x8x12, 5250, kiln"
                            }
                            rows={5}
                        />
                    </div>

                    {/* Error */}
                    {error && (
                        <div className="import-error">
                            <AlertCircle size={16} /> {error}
                        </div>
                    )}

                    {/* Success */}
                    {importResult && (
                        <div className="import-success">
                            <CheckCircle2 size={16} /> {importResult}
                        </div>
                    )}

                    {/* Step 3: Preview */}
                    {preview.length > 0 && (
                        <div className="import-step">
                            <div className="import-step-label">3. Review & confirm</div>

                            <div className="import-match-summary">
                                {updatedCount > 0 && (
                                    <span className="match-badge updated">
                                        <ArrowRightLeft size={12} /> {updatedCount} price updates
                                    </span>
                                )}
                                {newCount > 0 && (
                                    <span className="match-badge new">
                                        <PlusCircle size={12} /> {newCount} new items
                                    </span>
                                )}
                                {matchedCount > 0 && (
                                    <span className="match-badge matched">
                                        <CheckCircle2 size={12} /> {matchedCount} unchanged
                                    </span>
                                )}
                            </div>

                            <div className="import-preview-table-wrap">
                                <table className="materials-table import-preview-table">
                                    <thead>
                                        <tr>
                                            <th>Status</th>
                                            <th>Dimensions</th>
                                            <th>Dryness</th>
                                            <th>Board Feet</th>
                                            <th>Price</th>
                                            <th>$/bf</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {preview.map((row, idx) => (
                                            <tr key={idx} className={`preview-row-${row.status}`}>
                                                <td>
                                                    {row.status === 'new' && (
                                                        <span className="status-pill new">New</span>
                                                    )}
                                                    {row.status === 'updated' && (
                                                        <span className="status-pill updated">
                                                            {row.oldPiecePrice != null
                                                                ? <>Δ {row.priceChangePct! > 0 ? '+' : ''}{row.priceChangePct}%</>
                                                                : '+ Supplier'
                                                            }
                                                        </span>
                                                    )}
                                                    {row.status === 'matched' && (
                                                        <span className="status-pill matched">Same</span>
                                                    )}
                                                </td>
                                                <td><strong>{row.dimensions}</strong></td>
                                                <td>{drynessPreviewLabel(row.moistureContent)}</td>
                                                <td>{row.boardFeet.toFixed(2)}</td>
                                                <td>
                                                    {row.status === 'updated' && row.oldPiecePrice != null ? (
                                                        <span className="price-change">
                                                            <span className="old-price">{formatCurrency(row.oldPiecePrice)}</span>
                                                            {' → '}
                                                            <span className="new-price">{formatCurrency(row.piecePrice)}</span>
                                                        </span>
                                                    ) : (
                                                        formatCurrency(row.piecePrice)
                                                    )}
                                                </td>
                                                <td>{formatCurrency(row.costPerBdFt)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                <div className="import-modal-footer">
                    <button className="materials-action-btn secondary" onClick={handleClose}>
                        {importResult ? 'Done' : 'Cancel'}
                    </button>
                    {!importResult && (
                        <button
                            className="materials-action-btn primary"
                            onClick={handleImport}
                            disabled={
                                importing ||
                                preview.length === 0 ||
                                !supplier ||
                                !species ||
                                (newCount === 0 && updatedCount === 0)
                            }
                        >
                            {importing
                                ? 'Importing...'
                                : `Import ${newCount + updatedCount} item${newCount + updatedCount !== 1 ? 's' : ''}`
                            }
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SupplierImportModal;
